import { SubsidyFieldsFragment } from 'api/graphql-types';
import clsx from 'clsx';
import isPast from 'date-fns/isPast';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { BaseModule } from 'modules/base-module/base-module';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { getDateFnsLocale, getFormattedDate } from 'utils/date';
import styles from './subsidy-header.module.scss';

interface ISubsidyHeaderProps {
	module?: SubsidyFieldsFragment;
}

const SubsidyHeader = (props: ISubsidyHeaderProps): React.JSX.Element => {
	const intl = useIntl();
	const router = useRouter();
	const { locale } = router;

	const formattedEndDate = `${getFormattedDate(
		new Date(props?.module?.endDate),
		'd-M-y',
		locale
	)} (${formatDistanceToNow(new Date(props?.module?.endDate), {
		addSuffix: false,
		includeSeconds: false,
		locale: getDateFnsLocale(locale)
	})})`;
	const endDateHasPassed = isPast(new Date(props?.module?.endDate));

	return (
		<BaseModule
			defaultModuleOptions={{
				backgroundColor: 'white',
				padding: 'medium'
			}}
		>
			<div className={styles.wrapper}>
				<div>
					<span className={styles.title}>{intl.formatMessage({ id: 'modules.subsidy.header.status' })}: </span>
					<span>{intl.formatMessage({ id: getStatus(props?.module?.subsidystatus) })}</span>
				</div>
				<div>
					<span className={styles.title}>{intl.formatMessage({ id: 'modules.subsidy.header.endDate' })}: </span>
					{endDateHasPassed ? (
						<span>{intl.formatMessage({ id: 'modules.subsidy.header.endDate_passed' })}</span>
					) : (
						<>
							<span>{intl.formatMessage({ id: 'modules.subsidy.header.endDate_until' })}</span>
							<span className={styles.date}>{formattedEndDate}</span>
						</>
					)}
				</div>
				<div>
					<span className={styles.title}>{intl.formatMessage({ id: 'modules.subsidy.header.targetGroup' })}: </span>
					{props?.module?.targetGroupText ? (
						<span>{props?.module?.targetGroupText}</span>
					) : (
						<span>
							{props?.module?.target_groups?.map((item, index) => {
								return `${item?.data?.attributes?.name}${
									index === (props?.module?.target_groups?.length || 0) - 1 ? '' : ','
								} `;
							})}
						</span>
					)}
				</div>
				<div>
					<span
						className={clsx(
							styles.subsidyCeiling,
							props?.module?.subsidyCeiling === 'met'
								? styles.met
								: props?.module?.subsidyCeiling === 'almost_met' && styles.almost_met
						)}
					>
						{props?.module?.subsidyCeiling &&
							intl.formatMessage({ id: getSubsidyCeiling(props?.module?.subsidyCeiling) })}
					</span>
				</div>
			</div>
		</BaseModule>
	);
};

export { SubsidyHeader };

function getStatus(status: string | undefined): string | undefined {
	switch (status) {
		case 'open':
			return 'subsidy.status.open';

		case 'closed':
			return 'subsidy.status.closed';

		case 'almost_opened':
			return 'subsidy.status.almost_opened';

		default:
			return undefined;
	}
}

function getSubsidyCeiling(subsidyCeiling: SubsidyFieldsFragment['subsidyCeiling']): string | undefined {
	switch (subsidyCeiling) {
		case 'almost_met':
			return 'modules.subsidy.header.subsidyCeiling_almost_met';

		case 'met':
			return 'modules.subsidy.header.subsidyCeiling_met';

		default:
			return undefined;
	}
}
